/**
 * Center "Overall Average" gauge – two concentric donut rings (Me / Benchmark).
 * Outer ring = Me (green), inner ring = Benchmark (orange).
 */
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";

const BASE_SIZE = 220;

const DEFAULT_TRACK = "rgba(255,255,255,0.2)";
const DEFAULT_LABEL = "rgba(255,255,255,0.7)";
const DEFAULT_ME_COLOR = "#35c79a";
const DEFAULT_BENCHMARK_COLOR = "#f0825c";

export default function AverageGauge({
  variationA,
  variationB,
  labelA = "Me",
  labelB = "Benchmark",
  size = BASE_SIZE,
  palette,
  meColor = DEFAULT_ME_COLOR,
  benchmarkColor = DEFAULT_BENCHMARK_COLOR,
}) {
  const [animatedA, setAnimatedA] = useState(0);
  const [animatedB, setAnimatedB] = useState(0);
  const isLight = Boolean(palette);
  const trackStroke = isLight ? (palette.trackStroke ?? "rgba(0,0,0,0.12)") : DEFAULT_TRACK;
  const labelColor = isLight ? (palette.textSecondary ?? "#5a7a7d") : DEFAULT_LABEL;
  const valueColor = isLight ? (palette.gaugeValueColor ?? meColor) : meColor;
  const deltaPositive = isLight ? (palette.deltaPositive ?? { bg: "rgba(45, 212, 191, 0.15)", color: "#0f766e" }) : { bg: "rgba(45, 212, 191, 0.15)", color: "#5eead4" };
  const deltaNegative = isLight ? (palette.deltaNegative ?? { bg: "rgba(248, 113, 113, 0.15)", color: "#dc2626" }) : { bg: "rgba(248, 113, 113, 0.2)", color: "#f87171" };

  useEffect(() => {
    const t = setTimeout(() => {
      setAnimatedA(variationA);
      setAnimatedB(variationB);
    }, 100);
    return () => clearTimeout(t);
  }, [variationA, variationB]);

  const scale = size / BASE_SIZE;
  const strokeWidth = 14 * scale;
  const gap = 5 * scale;
  const radiusA = (size - strokeWidth) / 2;
  const radiusB = radiusA - strokeWidth - gap;
  const center = size / 2;
  const circumA = 2 * Math.PI * radiusA;
  const circumB = 2 * Math.PI * radiusB;
  const offsetA = circumA - (animatedA / 100) * circumA;
  const offsetB = circumB - (animatedB / 100) * circumB;

  const diff = variationA - variationB;
  const diffSign = diff >= 0 ? "+" : "";

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box position="relative">
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          sx={{
            borderRadius: "50%",
            filter: "blur(24px)",
            background: `${meColor}15`,
          }}
        />
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ position: "relative" }}
        >
          <circle
            cx={center}
            cy={center}
            r={radiusA}
            fill="none"
            stroke={trackStroke}
            strokeWidth={strokeWidth}
            opacity={0.5}
          />
          <circle
            cx={center}
            cy={center}
            r={radiusB}
            fill="none"
            stroke={trackStroke}
            strokeWidth={strokeWidth}
            opacity={0.5}
          />
          <circle
            cx={center}
            cy={center}
            r={radiusA}
            fill="none"
            stroke={meColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumA}
            strokeDashoffset={offsetA}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ transition: "all 1000ms ease-out" }}
          />
          <circle
            cx={center}
            cy={center}
            r={radiusB}
            fill="none"
            stroke={benchmarkColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumB}
            strokeDashoffset={offsetB}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ transition: "all 1000ms ease-out" }}
            opacity={0.85}
          />
          <text
            x={center}
            y={center - 22 * scale}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              fontSize: `${0.5 * scale}rem`,
              fill: labelColor,
              fontFamily: "Inter, Helvetica, Arial, sans-serif",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Overall Average
          </text>
          <text
            x={center}
            y={center + 4 * scale}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              fontSize: `${2.2 * scale}rem`,
              fill: valueColor,
              fontFamily: "Inter, Helvetica, Arial, sans-serif",
              fontWeight: 700,
            }}
          >
            {animatedA.toFixed(1)}%
          </text>
          <text
            x={center}
            y={center + 28 * scale}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              fontSize: `${0.7 * scale}rem`,
              fill: labelColor,
              fontFamily: "Inter, Helvetica, Arial, sans-serif",
            }}
          >
            vs {animatedB.toFixed(1)}%
          </text>
        </svg>
      </Box>
      <Box mt={1.5} display="flex" alignItems="center" gap={2.5}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: meColor,
            }}
          />
          <Box component="span" sx={{ fontSize: "0.75rem", color: labelColor }}>
            {labelA}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: benchmarkColor,
            }}
          />
          <Box component="span" sx={{ fontSize: "0.75rem", color: labelColor }}>
            {labelB}
          </Box>
        </Box>
        <Box
          component="span"
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontSize: "0.75rem",
            fontWeight: 600,
            fontFamily: "monospace",
            ...(diff >= 0
              ? { bgcolor: deltaPositive.bg, color: deltaPositive.color }
              : { bgcolor: deltaNegative.bg, color: deltaNegative.color }),
          }}
        >
          {diffSign}
          {diff.toFixed(1)}pp
        </Box>
      </Box>
    </Box>
  );
}
